import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Archive } from "lucide-react";
import { format } from "date-fns";
import { getMountainToday, getMountainNow } from "@/lib/dateUtils";
import { useAuth } from "@/auth/AuthContext";

export function NoteLog({ notes = [], onChange, className = "", isAddingExternal, onAddingChange, maxVisible, onArchive }) {
  const [newNote, setNewNote] = useState("");
  const [isAddingInternal, setIsAddingInternal] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { user } = useAuth();
  const author = user?.name || user?.email || "";

  const isAdding = isAddingExternal !== undefined ? isAddingExternal : isAddingInternal;
  const setIsAdding = onAddingChange || setIsAddingInternal;

  const entries = Array.isArray(notes) ? notes : [];
  const collapsed = maxVisible && !showAll && entries.length > maxVisible;
  const visible = collapsed ? entries.slice(0, maxVisible) : entries;

  const handleAdd = () => {
    if (!newNote.trim()) return;
    const entry = {
      date: getMountainToday(),
      text: newNote.trim(),
      by: author,
    };
    const updated = [entry, ...entries];
    onChange(updated);
    setNewNote("");
    setIsAdding(false);
  };

  const showInternalButton = isAddingExternal === undefined;

  return (
    <div className={className} data-testid="note-log">
      {showInternalButton && !isAdding ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full mb-2 text-xs"
          onClick={() => setIsAdding(true)}
          data-testid="btn-add-note"
        >
          <Plus className="w-3 h-3 mr-1" /> Add Note
        </Button>
      ) : !isAdding ? null : (
        <div className="mb-2 space-y-1">
          <div className="text-xs font-medium text-slate-400">{format(getMountainNow(), "MMM d, yyyy")}{author ? ` · ${author}` : ""}</div>
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={2}
            placeholder="Enter note..."
            autoFocus
            className="text-sm"
            data-testid="input-new-note"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAdd();
              }
              if (e.key === "Escape") {
                setIsAdding(false);
                setNewNote("");
              }
            }}
          />
          <div className="flex gap-1">
            <Button type="button" size="sm" className="h-7 text-xs" onClick={handleAdd} data-testid="btn-save-note">Save</Button>
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setIsAdding(false); setNewNote(""); }}>Cancel</Button>
          </div>
        </div>
      )}
      {entries.length > 0 ? (
        <>
          <div className={`space-y-2 ${maxVisible ? "" : "max-h-40 overflow-y-auto"}`}>
            {visible.map((entry, i) => (
              // eslint-disable-next-line react/no-array-index-key -- notes ordered newest-first; no stable id field, content can repeat across dates
              <div key={`note-${i}`} className="border-l-2 border-slate-200 pl-2 group flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-400">
                    {entry.date ? format(new Date(entry.date + "T00:00:00"), "MMM d, yyyy") : ""}{entry.by ? <span className="text-slate-500"> · {entry.by}</span> : null}
                  </div>
                  <div className="text-sm text-slate-700" title={entry.text}>{entry.text}</div>
                </div>
                {onArchive && (
                  <button
                    type="button"
                    title="Archive to History"
                    className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                    onClick={() => onArchive(i)}
                    data-testid={`btn-archive-note-${i}`}
                  >
                    <Archive className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {maxVisible && entries.length > maxVisible && (
            <button
              type="button"
              className="text-[11px] text-blue-600 font-medium hover:underline mt-1.5"
              onClick={() => setShowAll(s => !s)}
              data-testid="btn-notes-show-all"
            >
              {showAll ? "Show less" : `Show all ${entries.length} notes`}
            </button>
          )}
        </>
      ) : !isAdding && (
        <div className="text-xs text-slate-400 text-center py-2">No notes</div>
      )}
    </div>
  );
}
